import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Upload, X, Eye, Trash2, Image, FileText, Zap, Clock, GripVertical, Check } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { AdminSelect } from "./AdminSelect";

const GROUPS = ["Canvas", "Wall Murals", "Banners", "Stickers & Labels", "Vehicle Graphics", "General"];
const STATUS_OPTIONS = ["draft", "published"];

function FileThumbnail({ item }) {
  const [imgError, setImgError] = useState(false);

  const isImage =
    (item.content_type || "").startsWith("image/") ||
    /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(item.file_url || "");

  // Prefer inline base64 thumbnail; fall back to server path (relative /api/admin/uploads/...)
  const src = item.thumbnail || (isImage && item.file_url ? item.file_url : null);

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={item.title || ""}
        className="w-full h-full object-cover"
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center bg-white/[0.03]">
      {isImage ? <Image size={20} className="text-white/20" /> : <FileText size={20} className="text-white/20" />}
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

/* ── Artwork Uploader ── */
export function AdminArtworkUploader() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [group, setGroup] = useState("General");
  const [status, setStatus] = useState("draft");
  const [dragOver, setDragOver] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const inputRef = useRef(null);

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList).map((f) => ({ file: f, title: f.name, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }));
    setFiles((prev) => [...prev, ...arr]);
  };

  const removeFile = (idx) => {
    setFiles((prev) => { const next = [...prev]; if (next[idx].preview) URL.revokeObjectURL(next[idx].preview); next.splice(idx, 1); return next; });
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    const results = [];
    for (const item of files) {
      const fd = new FormData();
      fd.append("file", item.file);
      fd.append("title", item.title);
      fd.append("category", "artwork");
      fd.append("group", group);
      fd.append("status", status);
      try {
        const res = await adminApi.post("/api/admin/media/upload", fd);
        results.push({ name: item.file.name, ok: true, id: res.data.id });
      } catch (err) {
        results.push({ name: item.file.name, ok: false, error: err.response?.data?.detail || err.message });
      }
    }
    setUploadResults(results);
    setFiles([]);
    setUploading(false);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="admin-artwork-uploader">
      <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
        <Upload size={16} className="text-[#CBA052]" />
        <h3 className="text-sm font-bold text-white">Artwork Uploader</h3>
        <span className="ml-auto text-[10px] text-white/30 uppercase tracking-wider">Drag &amp; drop or browse</span>
      </div>

      <div className="p-5 space-y-5">
        {/* Group & status selectors */}
        <div className="flex flex-wrap gap-4">
          <label className="text-xs text-white/50">
            Group:
            <AdminSelect value={group} onChange={(e) => setGroup(e.target.value)} className="ml-2 inline-flex w-auto" data-testid="upload-group-select">
              {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
            </AdminSelect>
          </label>
          <label className="text-xs text-white/50">
            Status:
            <AdminSelect value={status} onChange={(e) => setStatus(e.target.value)} className="ml-2 inline-flex w-auto" data-testid="upload-status-select">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </AdminSelect>
          </label>
        </div>

        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={() => setDragOver(false)}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-[#CBA052] bg-[#CBA052]/5" : "border-white/10 hover:border-white/20"}`}
          data-testid="upload-drop-zone"
        >
          <Upload size={24} className={`mx-auto mb-3 ${dragOver ? "text-[#CBA052]" : "text-white/20"}`} />
          <p className="text-xs text-white/50">Drop files here or click to browse</p>
          <p className="text-[10px] text-white/25 mt-1">JPG, PNG, GIF, WebP, SVG, PDF, AI, EPS, TIFF — Max 25MB</p>
          <input ref={inputRef} type="file" multiple accept="image/*,.pdf,.ai,.eps,.tiff,.tif,.svg" onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid="upload-file-input" />
        </div>

        {/* File previews */}
        {files.length > 0 && (
          <div className="space-y-2" data-testid="upload-file-list">
            {files.map((item, idx) => (
              <div key={`${item.file.name}-${idx}`} className="flex items-center gap-3 border border-white/[0.06] bg-white/[0.02] rounded p-2">
                <div className="w-10 h-10 rounded overflow-hidden shrink-0">
                  {item.preview ? <img src={item.preview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-white/[0.04] flex items-center justify-center"><FileText size={14} className="text-white/20" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <input value={item.title} onChange={(e) => { const next = [...files]; next[idx] = { ...next[idx], title: e.target.value }; setFiles(next); }} className="w-full bg-transparent text-xs text-white border-none outline-none truncate" />
                  <p className="text-[10px] text-white/25">{formatBytes(item.file.size)}</p>
                </div>
                <button onClick={() => removeFile(idx)} className="text-white/20 hover:text-red-400 transition-colors"><X size={14} /></button>
              </div>
            ))}
            <button onClick={handleUpload} disabled={uploading} className="w-full py-2.5 bg-[#CBA052] text-black text-xs font-bold rounded transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="upload-submit-btn">
              {uploading ? "Uploading..." : `Upload ${files.length} file${files.length > 1 ? "s" : ""}`}
            </button>
          </div>
        )}

        {/* Upload results */}
        {uploadResults.length > 0 && (
          <div className="space-y-1" data-testid="upload-results">
            {uploadResults.map((r, i) => (
              <div key={`result-${r.name}-${i}`} className={`flex items-center gap-2 text-xs px-3 py-2 rounded ${r.ok ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                {r.ok ? <Check size={12} /> : <X size={12} />}
                <span className="truncate">{r.name}</span>
                {!r.ok && <span className="ml-auto text-[10px]">{r.error}</span>}
              </div>
            ))}
            <button onClick={() => setUploadResults([])} className="text-[10px] text-white/30 hover:text-white/50 mt-1">Clear</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Upload History ── */
export function AdminUploads() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ group: "", status: "" });

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.group) params.set("group", filter.group);
      if (filter.status) params.set("status", filter.status);
      const res = await adminApi.get(`/api/admin/media?${params.toString()}`);
      setMedia(res.data.media || []);
    } catch (err) { console.error("[AdminMedia] fetch failed:", err); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const toggleStatus = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    try {
      await adminApi.put(`/api/admin/media/${item.id}`, { status: next });
      fetchMedia();
    } catch (err) { console.error("[AdminMedia] toggle status failed:", err); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this upload?")) return;
    try {
      await adminApi.delete(`/api/admin/media/${id}`);
      fetchMedia();
    } catch (err) { console.error("[AdminMedia] delete failed:", err); }
  };

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="admin-uploads">
      <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
        <Clock size={16} className="text-[#CBA052]" />
        <h3 className="text-sm font-bold text-white">Upload History</h3>
        <span className="ml-auto text-[10px] text-white/30">{media.length} files</span>
      </div>

      <div className="p-5">
        <div className="flex flex-wrap gap-3 mb-4">
          <AdminSelect value={filter.group} onChange={(e) => setFilter((f) => ({ ...f, group: e.target.value }))} className="w-auto" data-testid="uploads-filter-group">
            <option value="">All groups</option>
            {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
          </AdminSelect>
          <AdminSelect value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} className="w-auto" data-testid="uploads-filter-status">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </AdminSelect>
        </div>

        {loading ? (
          <div className="text-xs text-white/30 text-center py-8">Loading uploads...</div>
        ) : media.length === 0 ? (
          <div className="text-xs text-white/30 text-center py-8" data-testid="uploads-empty">No uploads found. Use the uploader above to add artwork.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="uploads-grid">
            {media.map((item) => (
              <div key={item.id} className="group border border-white/[0.06] bg-white/[0.02] rounded overflow-hidden" data-testid={`upload-item-${item.id}`}>
                <div className="aspect-square relative">
                  <FileThumbnail item={item} />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => toggleStatus(item)} className="p-1.5 bg-white/10 rounded hover:bg-white/20" title={item.status === "published" ? "Unpublish" : "Publish"}>
                      <Eye size={12} className={item.status === "published" ? "text-green-400" : "text-white/50"} />
                    </button>
                    <button onClick={() => deleteItem(item.id)} className="p-1.5 bg-white/10 rounded hover:bg-red-500/30" title="Delete">
                      <Trash2 size={12} className="text-white/50" />
                    </button>
                  </div>
                  <span className={`absolute top-1 right-1 text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${item.status === "published" ? "bg-green-500/20 text-green-400" : "bg-white/10 text-white/30"}`}>
                    {item.status}
                  </span>
                </div>
                <div className="p-2">
                  <p className="text-[10px] text-white truncate" title={item.title}>{item.title}</p>
                  <p className="text-[9px] text-white/25">{item.group || "General"} &middot; {formatBytes(item.size_bytes)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Bulk Re-upload ── */
export function AdminBulkReupload() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [group, setGroup] = useState("General");
  const inputRef = useRef(null);

  const failedResults = useMemo(() => results.filter((r) => !r.ok), [results]);

  const handleFiles = (fileList) => setFiles(Array.from(fileList));

  const handleBulkUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    const res = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("category", "artwork");
      fd.append("group", group);
      fd.append("status", "published");
      try {
        await adminApi.post("/api/admin/media/upload", fd);
        res.push({ name: file.name, ok: true });
      } catch (err) {
        res.push({ name: file.name, ok: false, error: err.response?.data?.detail || err.message });
      }
    }
    setResults(res);
    setFiles([]);
    setUploading(false);
  };

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="admin-bulk-reupload">
      <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
        <Zap size={16} className="text-[#CBA052]" />
        <h3 className="text-sm font-bold text-white">Bulk Re-upload</h3>
        <span className="ml-auto text-[10px] text-white/30 uppercase tracking-wider">Batch publish</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-4">
          <label className="text-xs text-white/50">
            Group:
            <AdminSelect value={group} onChange={(e) => setGroup(e.target.value)} className="ml-2 inline-flex w-auto">
              {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
            </AdminSelect>
          </label>
          <button onClick={() => inputRef.current?.click()} className="text-xs text-[#CBA052] border border-[#CBA052]/30 rounded px-3 py-1.5 hover:bg-[#CBA052]/10 transition-colors" data-testid="bulk-select-btn">
            Select Files
          </button>
          <input ref={inputRef} type="file" multiple accept="image/*,.pdf,.ai,.eps,.tiff,.tif,.svg" onChange={(e) => handleFiles(e.target.files)} className="hidden" />
          {files.length > 0 && <span className="text-xs text-white/40">{files.length} selected</span>}
        </div>

        {files.length > 0 && (
          <button onClick={handleBulkUpload} disabled={uploading} className="py-2.5 px-6 bg-[#CBA052] text-black text-xs font-bold rounded transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="bulk-upload-btn">
            {uploading ? "Uploading..." : `Bulk Upload ${files.length} Files`}
          </button>
        )}

        {results.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs text-white/40">{results.filter((r) => r.ok).length}/{results.length} uploaded</p>
            {failedResults.map((r, i) => (
              <p key={`err-${i}`} className="text-[10px] text-red-400">{r.name}: {r.error}</p>
            ))}
            <button onClick={() => setResults([])} className="text-[10px] text-white/30 hover:text-white/50">Clear</button>
          </div>
        )}
      </div>
    </div>
  );
}
